'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { loginCustomer, registerCustomer } from '@/lib/auth';
import { setAuthToken, setAuthUser } from '@/lib/apollo-client-simple';

export default function AuthClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirror backend password policy (nexbeauty rest-api-security.php
  // validate_password): ≥8 chars, at least one letter + one number,
  // not a common word. Returns a user-facing error string or null.
  const validatePassword = (pw: string): string | null => {
    if (!pw) return 'Password is required.';
    if (pw.length < 8) return 'At least 8 characters.';
    if (!/[A-Za-z]/.test(pw)) return 'Must include a letter.';
    if (!/[0-9]/.test(pw)) return 'Must include a number.';
    const common = ['password', '12345678', 'qwerty123', 'admin123'];
    if (common.includes(pw.toLowerCase())) return 'Too common — pick something unique.';
    return null;
  };
  const passwordError = form.password ? validatePassword(form.password) : null;
  const passwordValid = !!form.password && !passwordError;
  const confirmMatches = !!form.confirmPassword && form.password === form.confirmPassword;
  const signupReady = passwordValid && confirmMatches && termsAgreed && !!form.name.trim() && !!form.email.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const email = form.email.trim();
    const password = form.password;
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    if (mode === 'signup') {
      const pwErr = validatePassword(password);
      if (pwErr) {
        setError(pwErr);
        return;
      }
      if (password !== form.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    } else if (!password) {
      setError('Password is required.');
      return;
    }
    if (mode === 'signup' && !termsAgreed) {
      setError('You must agree to the Terms of Service and Privacy Policy to create an account.');
      return;
    }
    setIsLoading(true);
    try {
      if (mode === 'login') {
        const result = await loginCustomer({ username: email, password });
        if (result.success && result.token) {
          setAuthToken(result.token);
          if (result.user) setAuthUser(result.user);
          const redirect = searchParams?.get('redirect') || '/account';
          router.push(redirect);
        } else {
          setError(result.message || 'Invalid email or password.');
          setIsLoading(false);
        }
      } else {
        const parts = form.name.trim().split(/\s+/);
        const firstName = parts[0] || '';
        const lastName = parts.slice(1).join(' ') || '';
        const result = await registerCustomer({ email, password, firstName, lastName });
        if (result.success) {
          if (result.token) {
            setAuthToken(result.token);
            if (result.user) setAuthUser(result.user);
            router.push('/account');
          } else {
            setMode('login');
            setError(result.message || 'Account created. Please sign in.');
            setIsLoading(false);
          }
        } else {
          setError(result.message || 'Registration failed. Please try again.');
          setIsLoading(false);
        }
      }
    } catch (err: any) {
      setError(err?.message || 'An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left: Brand image — explicit min-h-screen so Next.js <Image fill>
          has a sized parent to fill. Hidden on mobile (<768px) where the
          right-side form takes the full width. */}
      <div className="relative hidden md:block md:min-h-screen">
        <AppImage
          src="/assets/images/kym-products/kym-hero.jpg"
          alt="SweetSkyn by Kym natural body butter collection — handcrafted in small batches"
          fill
          priority
          sizes="50vw"
          className="object-cover" />

        {/* Soft scrim — just enough contrast for the white logo + copy in the corners */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/35 via-transparent to-black/40" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={36} />
            <span data-ocwp-field="brandName" className="font-display text-xl font-semibold text-white">SweetSkyn by Kym</span>
          </Link>
          <div>
            <p className="font-display text-4xl font-light text-white leading-tight mb-3">
              Your beauty ritual,<br />
              <em>elevated.</em>
            </p>
            <p className="text-white/70 text-sm">Join 50,000+ beauty lovers who shop SweetSkyn by Kym.</p>
          </div>
        </div>
      </div>
      {/* Right: Form */}
      <div className="flex flex-col justify-center px-8 md:px-16 py-12">
        {/* Mobile logo */}
        <div className="md:hidden mb-8">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={32} />
            <span data-ocwp-field="brandName" className="font-display text-lg font-semibold text-foreground">SweetSkyn by Kym</span>
          </Link>
        </div>

        <div className="max-w-sm w-full mx-auto">
          {/* Toggle */}
          <div className="flex mb-8 border-b border-border">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 pb-3 text-[11px] font-semibold uppercase tracking-widest transition-colors ${
              mode === 'login' ? 'text-foreground border-b-2 border-accent -mb-px' : 'text-muted-foreground'}`
              }>
              
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 pb-3 text-[11px] font-semibold uppercase tracking-widest transition-colors ${
              mode === 'signup' ? 'text-foreground border-b-2 border-accent -mb-px' : 'text-muted-foreground'}`
              }>
              
              Create Account
            </button>
          </div>

          <h1 className="font-display text-3xl font-light text-foreground mb-2">
            {mode === 'login' ? 'Welcome back' : 'Join SweetSkyn by Kym'}
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            {mode === 'login' ? 'Sign in to access your account, orders and wishlist.' : 'Create your account to start shopping luxury beauty.'}
          </p>

          {/* Social login (Google/Apple) hidden until platform-level SSO lands —
              see project_platform_sso.md. Per-merchant OAuth setup is too heavy
              for the SMB tier; platform will proxy OAuth so SSO is zero-config. */}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error &&
            <div className="p-3 border border-red-500/30 bg-red-50 text-[12px] text-red-600">
                {error}
              </div>
            }
            {mode === 'signup' &&
            <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Full Name</label>
                <input
                type="text"
                value={form?.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e?.target?.value }))}
                placeholder="Your name"
                className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />

              </div>
            }
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Email</label>
              <input
                type="email"
                value={form?.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e?.target?.value }))}
                placeholder="you@example.com"
                className="w-full border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring" />
              
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] font-semibold uppercase tracking-widest text-foreground">Password</label>
                {mode === 'login' &&
                <button className="text-[11px] text-accent hover:underline">Forgot password?</button>
                }
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form?.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e?.target?.value }))}
                  placeholder="••••••••"
                  className={`w-full border bg-background px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${
                    mode === 'signup' && form.password && passwordError
                      ? 'border-red-500'
                      : mode === 'signup' && passwordValid
                      ? 'border-green-500'
                      : 'border-border'
                  }`} />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}>

                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                </button>
              </div>
              {/* Signup requirements displayed BELOW the field. Each rule
                  lights up green as the user satisfies it so they aren't
                  playing a guessing game. */}
              {mode === 'signup' && (
                <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                  {([
                    { label: 'At least 8 characters', ok: form.password.length >= 8 },
                    { label: 'Includes a letter', ok: /[A-Za-z]/.test(form.password) },
                    { label: 'Includes a number', ok: /[0-9]/.test(form.password) },
                    { label: 'Not a common password', ok: !!form.password && !['password', '12345678', 'qwerty123', 'admin123'].includes(form.password.toLowerCase()) },
                  ]).map((r) => (
                    <li key={r.label} className={`flex items-center gap-1.5 ${r.ok ? 'text-green-600 font-medium' : ''}`}>
                      <span className="inline-block w-3">{r.ok ? '✓' : '•'}</span>
                      {r.label}
                    </li>
                  ))}
                </ul>
              )}
              {/* Single-line active error for accessibility + quick scan. */}
              {mode === 'signup' && form.password && passwordError && (
                <p className="text-[11px] text-red-600 mt-1.5 font-medium">✕ {passwordError}</p>
              )}
            </div>

            {mode === 'signup' &&
            <div>
                <label className="block text-[11px] font-semibold uppercase tracking-widest text-foreground mb-2">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={form?.confirmPassword}
                    onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e?.target?.value }))}
                    placeholder="••••••••"
                    className={`w-full border bg-background px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${
                      form.confirmPassword && form.password !== form.confirmPassword
                        ? 'border-red-500'
                        : form.confirmPassword && form.password === form.confirmPassword
                        ? 'border-green-500'
                        : 'border-border'
                    }`} />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}>
                    <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                  </button>
                </div>
                {/* Inline evaluation — only shown once the user has typed something. */}
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="text-[11px] text-red-600 mt-1.5">Passwords don't match.</p>
                )}
                {form.confirmPassword && form.password === form.confirmPassword && form.password.length >= 6 && (
                  <p className="text-[11px] text-green-600 mt-1.5">Passwords match.</p>
                )}
              </div>
            }

            {mode === 'signup' &&
            <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  required
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-accent" />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I agree to the{' '}
                  <a href="/terms" className="text-foreground underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" className="text-foreground underline">Privacy Policy</a>
                </span>
              </label>
            }

            <button
              type="submit"
              disabled={isLoading || (mode === 'signup' && !signupReady)}
              className="block w-full text-center py-3.5 bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-widest hover:bg-accent hover:text-accent-foreground transition-all duration-300 mt-2 disabled:opacity-60 disabled:cursor-not-allowed">

              {isLoading ? (mode === 'login' ? 'Signing in…' : 'Creating account…') : (mode === 'login' ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              className="text-foreground font-semibold hover:text-accent transition-colors">
              
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>

          <p className="text-center mt-6">
            <Link href="/checkout" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">
              Continue as guest →
            </Link>
          </p>
        </div>
      </div>
    </div>);

}